import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BuildingOfficeIcon, CheckCircleIcon, UserIcon } from '@heroicons/react/24/outline';

import { useNewFilters } from 'hooks/useFilters';
import { useConnectedPartners, useCurrentPartner, useTeamMembers } from 'services/repositories/partners/partners';
import { addPartnersToFiltersConfig } from 'support/helpers/filters/filters';
import { PropertyFilter, PropertyFilterType } from 'support/types/filters';

import { InvoiceVerificationStatus } from '../types';

export enum InvoiceVerificationFiltersKeys {
  documentNumber = 'document_number',
  partnerId = 'sender_partner_id',
  status = 'status',
  dueDate = 'due_date',
  responsibleUser = 'responsible_user_id',
}

export const useInvoiceVerificationFilters = () => {
  const { t } = useTranslation();
  const { data: currentPartner } = useCurrentPartner();
  const { data: teamMembers } = useTeamMembers({
    variables: {
      partnerId: currentPartner?.id,
    },
  });
  const defaultFilterConfig = useMemo<Array<PropertyFilter>>(
    () => [
      {
        key: InvoiceVerificationFiltersKeys.documentNumber,
        type: PropertyFilterType.search,
        placeholder: t('invoiceVerification:list.filter.search.label'),
        value: '',
      },
      {
        key: InvoiceVerificationFiltersKeys.responsibleUser,
        type: PropertyFilterType.dropdown,
        label: t('invoiceVerification:list.filter.responsibleUser.label'),
        Icon: UserIcon,
        value: '',
        dropDownOptions: {
          dropdownValues:
            teamMembers?.map((user) => ({
              label: user.name,
              value: user.id,
            })) || [],
        },
      },
      {
        key: InvoiceVerificationFiltersKeys.partnerId,
        type: PropertyFilterType.dropdown,
        label: t('invoiceVerification:list.filter.partnerId.label'),
        Icon: BuildingOfficeIcon,
        value: '',
        dropDownOptions: {
          dropdownValues: [],
        },
        eventDetails: {
          event: 'filter_changed',
          properties: {
            filter_type: 'partner',
          },
        },
      },
      {
        key: InvoiceVerificationFiltersKeys.status,
        type: PropertyFilterType.dropdown,
        label: t('invoiceVerification:list.filter.status.label'),
        Icon: CheckCircleIcon,
        value: '',
        dropDownOptions: {
          dropdownValues: [
            {
              label: t(`invoiceVerification:status.${InvoiceVerificationStatus.MATCHED}`),
              value: InvoiceVerificationStatus.MATCHED,
            },
            {
              label: t(`invoiceVerification:status.${InvoiceVerificationStatus.AWAITING_DELIVERY_DATA}`),
              value: InvoiceVerificationStatus.AWAITING_DELIVERY_DATA,
            },
            {
              label: t(`invoiceVerification:status.${InvoiceVerificationStatus.REQUIRES_ATTENTION}`),
              value: InvoiceVerificationStatus.REQUIRES_ATTENTION,
            },
          ],
          multiple: true,
        },
      },
      {
        key: InvoiceVerificationFiltersKeys.dueDate,
        type: PropertyFilterType.date,
        placeholder: t('invoiceVerification:list.filter.dueDate.label'),
        value: '',
      },
    ],
    [t, teamMembers],
  );

  const { data: connectedPartners } = useConnectedPartners();
  const filterConfig = useMemo(
    () => addPartnersToFiltersConfig(defaultFilterConfig, InvoiceVerificationFiltersKeys.partnerId, connectedPartners),
    [connectedPartners, defaultFilterConfig],
  );

  const { filters, filterValues, updateFilters, clearAllFilters, perPage } = useNewFilters(
    filterConfig,
    'invoice-verification',
  );

  return { filters, filterValues, updateFilters, clearAllFilters, perPage };
};
